import { MOMENT_TYPES } from 'utils/constants';

/** Menu order: Product Release with Marketing, without Marketing, Marketing Moment. */
export const ROADMAP_MOMENT_TYPE_MENU_NAMES = [
  MOMENT_TYPES.MARKETING_MOMENT_AND_PRODUCT_RELEASE,
  MOMENT_TYPES.PRODUCT_RELEASE,
  MOMENT_TYPES.MARKETING_MOMENT
] as const;

/** Milestones grid includes PCO and EOL in addition to roadmap moment types. */
export const MILESTONES_GRID_MOMENT_TYPE_MENU_NAMES = [
  ...ROADMAP_MOMENT_TYPE_MENU_NAMES,
  MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS,
  MOMENT_TYPES.END_OF_LIFE
] as const;

export type RoadmapMomentTypeMenuOptionsParams = {
  /** End of Life (EOL) is roadmap-only; omit on milestones grid report. Default true. */
  includeEol?: boolean;
  /** Price Changes and Offers + EOL for milestones grid report. Default false. */
  milestonesGridMenu?: boolean;
};

function getMenuNamesForContext({
  includeEol = true,
  milestonesGridMenu = false
}: RoadmapMomentTypeMenuOptionsParams = {}): readonly string[] {
  if (milestonesGridMenu) {
    return MILESTONES_GRID_MOMENT_TYPE_MENU_NAMES;
  }
  return includeEol
    ? [...ROADMAP_MOMENT_TYPE_MENU_NAMES, MOMENT_TYPES.END_OF_LIFE]
    : ROADMAP_MOMENT_TYPE_MENU_NAMES;
}

/**
 * Builds filter menu options from `rsdForms.momentTypes`: include only types whose
 * `name` matches the roadmap moment-type menu (and optionally EOL), in fixed order.
 */
export function getRoadmapMomentTypeMenuOptions(
  momentTypes: { id: string; name: string }[],
  params?: RoadmapMomentTypeMenuOptionsParams
): { key: string; label: string }[] {
  return getMenuNamesForContext(params).flatMap(canonicalName => {
    const mt = momentTypes.find(m => m.name === canonicalName);
    return mt ? [{ key: mt.id, label: mt.name }] : [];
  });
}

/** Milestones grid: ignore EOL selections saved from the roadmap route. */
export function excludeEolFromMomentTypeSelection(
  selectedIds: string[],
  momentTypes: { id: string; name: string }[]
): string[] {
  const eolId = momentTypes.find(m => m.name === MOMENT_TYPES.END_OF_LIFE)?.id;
  if (!eolId) return selectedIds;
  return selectedIds.filter(id => id !== eolId);
}

/**
 * Moment type filter: selected values are ids from `rsdForms.momentTypes`.
 * A card matches if its `momentType` or `projectType` string equals the
 * corresponding `name` from that Redux list (no extra mapping layer).
 */
export function subProjectMatchesMomentTypeSelection(
  subProject: { projectType?: string; momentType?: string },
  selectedIds: string[],
  momentTypes: { id: string; name: string }[]
): boolean {
  if (selectedIds.length === 0) return true;

  const raw = (subProject.momentType || subProject.projectType || '').trim();
  if (!raw) return true;

  for (const id of selectedIds) {
    const def = momentTypes.find(mt => mt.id === id);
    if (def && def.name === raw) return true;
  }
  return false;
}
